// ============================================================================
// CHANGO V5.0 — Ops CMS editor pure helpers (Task #51)
// ============================================================================
// Mirrors the server's siteBlockSchema.js validation for the Phase-1 block
// kinds (hero, pricing) + the site_notices upsert shape. Keeping a client-side
// copy lets the editor reject bad input before it ever reaches the network,
// and keeps the editor tests hermetic (no DOM, no fetch).
//
// Any drift between this file and server/lib/siteBlockSchema.js is a smell —
// the build log flags that. The server remains the source of truth.
//
// Return shape (same as server helpers):
//   { ok: true,  value: <normalised> }
//   { ok: false, field: string, message: string }
// ============================================================================

// Keep in sync with server/lib/siteBlockSchema.js LIMITS.
export const LIMITS = Object.freeze({
  SHORT_TEXT: 200,
  MED_TEXT:   500,
  LONG_TEXT:  5000,
  URL:        500,
  CTA_LABEL:   80,
  SKU_CODE:    40,
  ICON:        80,
  MAX_PLANS:    6,
  MAX_HIGHLIGHTS: 20,
  MAX_FEATURES: 24,
  MAX_TESTIMONIALS: 12,
  MAX_FAQ:     40,
  // Both rich_text.body_md and site_notices.body_md share the 30_000 cap.
  RICH_TEXT_BODY: 30_000,
  NOTICE_BODY: 30_000,
});

// Keep in sync with server/lib/siteBlockSchema.js BLOCK_KINDS.
export const BLOCK_KINDS = Object.freeze([
  'hero', 'features', 'pricing', 'testimonials',
  'cta', 'rich_text', 'media', 'faq',
]);

// Media block enum — matches server siteBlockSchema.js validateMedia.
export const MEDIA_TYPES = Object.freeze(['image', 'video', 'embed']);

// Keep in sync with site_notices.severity ENUM.
export const NOTICE_SEVERITIES = Object.freeze(['info', 'success', 'warning', 'critical']);
// Keep in sync with site_notices.status ENUM, but only the ones the editor may set.
export const NOTICE_STATUS_EDITABLE = Object.freeze(['draft', 'scheduled', 'live']);

// ---- hero ------------------------------------------------------------------

export function normalizeHero(raw) {
  const r = raw || {};
  const title = str(r.title, 'title', { required: true, max: LIMITS.SHORT_TEXT });
  if (title.err) return title.err;
  const subtitle = str(r.subtitle, 'subtitle', { max: LIMITS.MED_TEXT });
  if (subtitle.err) return subtitle.err;
  const bg = str(r.bg_image_url, 'bg_image_url', { max: LIMITS.URL });
  if (bg.err) return bg.err;
  const primary = cta(r.primary_cta, 'primary_cta');
  if (primary.err) return primary.err;
  const secondary = cta(r.secondary_cta, 'secondary_cta');
  if (secondary.err) return secondary.err;
  return ok({
    title: title.value,
    subtitle: subtitle.value,
    bg_image_url: bg.value,
    primary_cta: primary.value,
    secondary_cta: secondary.value,
  });
}

// ---- pricing ---------------------------------------------------------------

export function normalizePricing(raw) {
  const r = raw || {};
  if (!Array.isArray(r.plans)) return fail('plans', 'plans 必须是数组');
  if (r.plans.length === 0)    return fail('plans', '至少需要 1 个方案');
  if (r.plans.length > LIMITS.MAX_PLANS) {
    return fail('plans', `最多 ${LIMITS.MAX_PLANS} 个方案`);
  }
  const plans = [];
  for (let i = 0; i < r.plans.length; i++) {
    const pl = r.plans[i] || {};
    const sku = str(pl.sku_code, `plans[${i}].sku_code`,
      { required: true, max: LIMITS.SKU_CODE });
    if (sku.err) return sku.err;
    const name = str(pl.name, `plans[${i}].name`,
      { required: true, max: LIMITS.SHORT_TEXT });
    if (name.err) return name.err;
    const tagline = str(pl.tagline, `plans[${i}].tagline`, { max: LIMITS.MED_TEXT });
    if (tagline.err) return tagline.err;
    const hl = strArr(pl.highlights, `plans[${i}].highlights`,
      { maxLen: LIMITS.SHORT_TEXT, maxItems: LIMITS.MAX_HIGHLIGHTS });
    if (hl.err) return hl.err;
    const c = cta(pl.cta, `plans[${i}].cta`);
    if (c.err) return c.err;
    plans.push({
      sku_code: sku.value,
      name: name.value,
      tagline: tagline.value,
      highlights: hl.value,
      cta: c.value,
      featured: Boolean(pl.featured),
    });
  }
  return ok({ plans });
}

// ---- Phase-2 block kinds (Task #52) ----------------------------------------
// features / testimonials / cta / rich_text / media / faq
//
// Each normalizer mirrors server/lib/siteBlockSchema.js so client-side drift
// is caught by the shared test table. Return shape is the same as the Phase-1
// helpers: { ok: true, value } | { ok: false, field, message }.

export function normalizeFeatures(raw) {
  const r = raw || {};
  const heading = str(r.heading, 'heading', { max: LIMITS.SHORT_TEXT });
  if (heading.err) return heading.err;
  if (!Array.isArray(r.items)) return fail('items', 'items 必须是数组');
  if (r.items.length === 0)    return fail('items', '至少需要 1 个特性');
  if (r.items.length > LIMITS.MAX_FEATURES) {
    return fail('items', `最多 ${LIMITS.MAX_FEATURES} 个特性`);
  }
  const items = [];
  for (let i = 0; i < r.items.length; i++) {
    const it = r.items[i] || {};
    const label = str(it.label, `items[${i}].label`,
      { required: true, max: LIMITS.SHORT_TEXT });
    if (label.err) return label.err;
    const desc = str(it.description, `items[${i}].description`,
      { max: LIMITS.MED_TEXT });
    if (desc.err) return desc.err;
    const icon = str(it.icon, `items[${i}].icon`, { max: LIMITS.ICON });
    if (icon.err) return icon.err;
    items.push({
      label: label.value,
      description: desc.value,
      icon: icon.value,
    });
  }
  return ok({ heading: heading.value, items });
}

export function normalizeTestimonials(raw) {
  const r = raw || {};
  if (!Array.isArray(r.items)) return fail('items', 'items 必须是数组');
  if (r.items.length === 0)    return fail('items', '至少需要 1 条证言');
  if (r.items.length > LIMITS.MAX_TESTIMONIALS) {
    return fail('items', `最多 ${LIMITS.MAX_TESTIMONIALS} 条证言`);
  }
  const items = [];
  for (let i = 0; i < r.items.length; i++) {
    const t = r.items[i] || {};
    const quote = str(t.quote, `items[${i}].quote`,
      { required: true, max: LIMITS.LONG_TEXT });
    if (quote.err) return quote.err;
    const name = str(t.name, `items[${i}].name`,
      { required: true, max: LIMITS.SHORT_TEXT });
    if (name.err) return name.err;
    const title = str(t.title, `items[${i}].title`, { max: LIMITS.SHORT_TEXT });
    if (title.err) return title.err;
    const avatar = str(t.avatar_url, `items[${i}].avatar_url`, { max: LIMITS.URL });
    if (avatar.err) return avatar.err;
    items.push({
      quote: quote.value,
      name: name.value,
      title: title.value,
      avatar_url: avatar.value,
    });
  }
  return ok({ items });
}

export function normalizeCtaBlock(raw) {
  const r = raw || {};
  const title = str(r.title, 'title', { required: true, max: LIMITS.SHORT_TEXT });
  if (title.err) return title.err;
  const subtitle = str(r.subtitle, 'subtitle', { max: LIMITS.MED_TEXT });
  if (subtitle.err) return subtitle.err;
  const primary = cta(r.primary_cta, 'primary_cta');
  if (primary.err) return primary.err;
  // The server insists on at least a primary CTA (otherwise it's just text).
  if (!primary.value) return fail('primary_cta', 'CTA 块必须至少填主按钮');
  return ok({
    title: title.value,
    subtitle: subtitle.value,
    primary_cta: primary.value,
  });
}

export function normalizeRichText(raw) {
  const r = raw || {};
  const body = str(r.body_md, 'body_md',
    { required: true, max: LIMITS.RICH_TEXT_BODY });
  if (body.err) return body.err;
  return ok({ body_md: body.value });
}

export function normalizeMedia(raw) {
  const r = raw || {};
  if (!MEDIA_TYPES.includes(r.media_type)) {
    return fail('media_type', `media_type 必须是 ${MEDIA_TYPES.join('/')}`);
  }
  const url = str(r.url, 'url', { required: true, max: LIMITS.URL });
  if (url.err) return url.err;
  if (!isSafeHref(url.value)) {
    return fail('url', '链接必须是 http(s) 或站内相对路径');
  }
  const caption = str(r.caption, 'caption', { max: LIMITS.MED_TEXT });
  if (caption.err) return caption.err;
  const alt = str(r.alt_text, 'alt_text', { max: LIMITS.MED_TEXT });
  if (alt.err) return alt.err;
  return ok({
    media_type: r.media_type,
    url: url.value,
    caption: caption.value,
    alt_text: alt.value,
  });
}

export function normalizeFaq(raw) {
  const r = raw || {};
  if (!Array.isArray(r.items)) return fail('items', 'items 必须是数组');
  if (r.items.length === 0)    return fail('items', '至少需要 1 条 FAQ');
  if (r.items.length > LIMITS.MAX_FAQ) {
    return fail('items', `最多 ${LIMITS.MAX_FAQ} 条 FAQ`);
  }
  const items = [];
  for (let i = 0; i < r.items.length; i++) {
    const f = r.items[i] || {};
    const q = str(f.question, `items[${i}].question`,
      { required: true, max: LIMITS.SHORT_TEXT });
    if (q.err) return q.err;
    const a = str(f.answer_md, `items[${i}].answer_md`,
      { required: true, max: LIMITS.LONG_TEXT });
    if (a.err) return a.err;
    items.push({ question: q.value, answer_md: a.value });
  }
  return ok({ items });
}

// Unified dispatcher — lets OpsCmsPageEditor validate a block without a
// switch/case in every caller. Unknown kinds pass through untouched so the
// editor can still round-trip data shapes it doesn't know about.
export function normalizeBlock(kind, payload) {
  switch (kind) {
    case 'hero':         return normalizeHero(payload);
    case 'pricing':      return normalizePricing(payload);
    case 'features':     return normalizeFeatures(payload);
    case 'testimonials': return normalizeTestimonials(payload);
    case 'cta':          return normalizeCtaBlock(payload);
    case 'rich_text':    return normalizeRichText(payload);
    case 'media':        return normalizeMedia(payload);
    case 'faq':          return normalizeFaq(payload);
    default:             return ok(payload ?? {});
  }
}

// ---- notice ----------------------------------------------------------------

export function normalizeNotice(raw) {
  const r = raw || {};
  const key = str(r.key_code, 'key_code', { required: true, max: 80 });
  if (key.err) return key.err;
  if (!/^[a-zA-Z0-9_.-]+$/.test(key.value)) {
    return fail('key_code', 'key_code 仅允许字母/数字/_.-');
  }
  const title = str(r.title, 'title', { required: true, max: LIMITS.SHORT_TEXT });
  if (title.err) return title.err;
  const severity = NOTICE_SEVERITIES.includes(r.severity) ? r.severity : 'info';
  const body = str(r.body_md, 'body_md', { max: LIMITS.NOTICE_BODY });
  if (body.err) return body.err;
  const actionLabel = str(r.action_label, 'action_label', { max: LIMITS.CTA_LABEL });
  if (actionLabel.err) return actionLabel.err;
  const actionHref  = str(r.action_href, 'action_href', { max: LIMITS.URL });
  if (actionHref.err) return actionHref.err;
  if (actionLabel.value && !actionHref.value) {
    return fail('action_href', '填了动作文案就需要动作链接');
  }
  if (actionHref.value && !isSafeHref(actionHref.value)) {
    return fail('action_href', '链接必须是 http(s) 或站内相对路径');
  }
  const status = NOTICE_STATUS_EDITABLE.includes(r.status) ? r.status : 'draft';
  const locale = typeof r.locale === 'string' && r.locale.length <= 10 && r.locale.length >= 2
    ? r.locale : 'zh-CN';
  // Window — editor passes ISO strings or null.
  const startsAt = isoOrNull(r.starts_at, 'starts_at');
  if (startsAt.err) return startsAt.err;
  const endsAt = isoOrNull(r.ends_at, 'ends_at');
  if (endsAt.err) return endsAt.err;
  if (startsAt.value && endsAt.value
      && new Date(endsAt.value).getTime() <= new Date(startsAt.value).getTime()) {
    return fail('ends_at', '结束时间必须晚于开始时间');
  }
  const dismissible = r.dismissible === false ? false : true;

  return ok({
    key_code: key.value,
    title: title.value,
    severity,
    body_md: body.value,
    action_label: actionLabel.value,
    action_href: actionHref.value,
    status,
    locale,
    starts_at: startsAt.value,
    ends_at: endsAt.value,
    dismissible,
  });
}

// ---- shared small utilities ------------------------------------------------

export function formatDateTime(v) {
  if (v == null) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
       + `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// `datetime-local` renders as 'YYYY-MM-DDTHH:mm' without a timezone. We round-
// trip via the editor by storing a best-effort ISO string (local → UTC) so the
// server receives something `new Date(...)` can parse.
export function localInputToIso(v) {
  if (v == null || v === '') return null;
  const d = new Date(v); // browser treats datetime-local as local time
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function isoToLocalInput(v) {
  if (v == null || v === '') return '';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
       + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ---- internal --------------------------------------------------------------

function str(v, field, { required = false, max = LIMITS.SHORT_TEXT } = {}) {
  if (v === null || v === undefined || v === '') {
    if (required) return { err: fail(field, `${field} 不能为空`) };
    return { value: null };
  }
  if (typeof v !== 'string') {
    return { err: fail(field, `${field} 必须是字符串`) };
  }
  const s = v.trim();
  if (s.length === 0) {
    if (required) return { err: fail(field, `${field} 不能为空`) };
    return { value: null };
  }
  if (s.length > max) {
    return { err: fail(field, `${field} 太长 (上限 ${max})`) };
  }
  return { value: s };
}

function strArr(v, field, { maxLen, maxItems }) {
  if (v === null || v === undefined) return { value: [] };
  if (!Array.isArray(v)) return { err: fail(field, `${field} 必须是数组`) };
  if (v.length > maxItems) {
    return { err: fail(field, `${field} 超过上限 ${maxItems}`) };
  }
  const out = [];
  for (let i = 0; i < v.length; i++) {
    const r = str(v[i], `${field}[${i}]`, { required: true, max: maxLen });
    if (r.err) return { err: r.err };
    out.push(r.value);
  }
  return { value: out };
}

function cta(v, field) {
  if (v === null || v === undefined) return { value: null };
  if (typeof v !== 'object' || Array.isArray(v)) {
    return { err: fail(field, `${field} 必须是对象`) };
  }
  // Treat an all-empty CTA as "no CTA" so editors can delete without
  // unchecking a box.
  const empty = !v.label && !v.href;
  if (empty) return { value: null };
  const label = str(v.label, `${field}.label`, { required: true, max: LIMITS.CTA_LABEL });
  if (label.err) return { err: label.err };
  const href = str(v.href, `${field}.href`, { required: true, max: LIMITS.URL });
  if (href.err) return { err: href.err };
  if (!isSafeHref(href.value)) {
    return { err: fail(`${field}.href`, '链接必须是 http(s) 或站内相对路径') };
  }
  return {
    value: {
      label: label.value,
      href: href.value,
      target: v.target === '_blank' ? '_blank' : '_self',
    },
  };
}

function isoOrNull(v, field) {
  if (v === null || v === undefined || v === '') return { value: null };
  if (typeof v !== 'string') return { err: fail(field, `${field} 必须是 ISO 字符串`) };
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return { err: fail(field, `${field} 不是有效时间`) };
  return { value: d.toISOString() };
}

// Only http(s) or "/", "./", "../" relative paths. Blocks `javascript:` + data URIs.
export function isSafeHref(href) {
  if (typeof href !== 'string' || href.length === 0) return false;
  const s = href.trim();
  if (s.startsWith('/') || s.startsWith('./') || s.startsWith('../')) return true;
  // Relative "foo/bar" anchors are allowed; a colon before a slash signals a scheme.
  const colonIx = s.indexOf(':');
  const slashIx = s.indexOf('/');
  if (colonIx === -1) return true;
  if (slashIx !== -1 && slashIx < colonIx) return true;
  // There is a scheme; must be http or https only.
  return /^https?:\/\//i.test(s);
}

function ok(value)             { return { ok: true, value }; }
function fail(field, message)  { return { ok: false, field, message }; }

export default {
  normalizeHero,
  normalizePricing,
  normalizeFeatures,
  normalizeTestimonials,
  normalizeCtaBlock,
  normalizeRichText,
  normalizeMedia,
  normalizeFaq,
  normalizeBlock,
  normalizeNotice,
  formatDateTime,
  localInputToIso,
  isoToLocalInput,
  isSafeHref,
  LIMITS,
  BLOCK_KINDS,
  MEDIA_TYPES,
  NOTICE_SEVERITIES,
  NOTICE_STATUS_EDITABLE,
};
